"use client";

import { c as _c } from "react/compiler-runtime";
import { ChevronDown } from "lucide-react";
import { useParams, useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { locales } from "@/config";
import { Link, usePathname } from "@/i18n/navigation";
import { cn } from "@/lib/utils";
const localeLabels: Record<(typeof locales)[number], string> = {
  da: "🇩🇰 DA",
  de: "🇩🇪 DE",
  en: "🇺🇸 EN",
  es: "🇪🇸 ES",
  fr: "🇫🇷 FR",
  it: "🇮🇹 IT",
  pt: "🇵🇹 PT",
  el: "🇬🇷 EL",
  nl: "🇳🇱 NL",
  fi: "🇫🇮 FI",
  is: "🇮🇸 IS",
  fo: "🇫🇴 FO",
  no: "🇳🇴 NO",
  sv: "🇸🇪 SV"
};
export const localeFlags: Record<(typeof locales)[number], string> = {
  da: "🇩🇰",
  de: "🇩🇪",
  en: "🇺🇸",
  es: "🇪🇸",
  fr: "🇫🇷",
  it: "🇮🇹",
  pt: "🇵🇹",
  el: "🇬🇷",
  nl: "🇳🇱",
  fi: "🇫🇮",
  is: "🇮🇸",
  fo: "🇫🇴",
  no: "🇳🇴",
  sv: "🇸🇪"
};
interface RouteLanguageSwitcherProps {
  align?: "start" | "center" | "end";
  triggerClassName?: string;
  labelMode?: "full" | "flag" | "responsive";
}
type AppLocale = (typeof locales)[number];
function useCurrentLocale() {
  const $ = _c(2);
  const params = useParams();
  let t0;
  if ($[0] !== params) {
    t0 = typeof params?.locale === "string" && locales.includes(params.locale as AppLocale) ? params.locale as AppLocale : "en";
    $[0] = params;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
interface SwitcherTriggerProps extends React.ComponentProps<"button"> {
  currentLocale: AppLocale;
  labelMode: NonNullable<RouteLanguageSwitcherProps["labelMode"]>;
  triggerClassName?: string;
}

// The dropdown's trigger. Radix passes its handlers and ref through `props`.
function SwitcherTrigger(t0) {
  const $ = _c(17);
  let className;
  let currentLocale;
  let labelMode;
  let props;
  let triggerClassName;
  if ($[0] !== t0) {
    ({
      currentLocale,
      labelMode,
      triggerClassName,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = currentLocale;
    $[3] = labelMode;
    $[4] = props;
    $[5] = triggerClassName;
  } else {
    className = $[1];
    currentLocale = $[2];
    labelMode = $[3];
    props = $[4];
    triggerClassName = $[5];
  }
  let t1;
  if ($[6] !== className || $[7] !== triggerClassName) {
    t1 = cn("inline-flex items-center gap-1 rounded-md border border-border bg-background px-3 py-2 text-sm font-medium shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground", triggerClassName, className);
    $[6] = className;
    $[7] = triggerClassName;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[9] !== currentLocale || $[10] !== labelMode) {
    t2 = labelMode === "responsive" ? <><span className="md:hidden">{localeFlags[currentLocale]}</span><span className="hidden md:inline">{localeLabels[currentLocale]}</span></> : <span>{labelMode === "flag" ? localeFlags[currentLocale] : localeLabels[currentLocale]}</span>;
    $[9] = currentLocale;
    $[10] = labelMode;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ChevronDown className="h-4 w-4" />;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== props || $[14] !== t1 || $[15] !== t2) {
    t4 = <button type="button" className={t1} {...props}>{t2}{t3}</button>;
    $[13] = props;
    $[14] = t1;
    $[15] = t2;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  return t4;
}

/**
 * The closed switcher, for a Suspense fallback around RouteLanguageSwitcher on
 * prerendered pages: the switcher keeps the query string in its links, and
 * useSearchParams has no value until the page runs in the browser.
 */
export function RouteLanguageSwitcherFallback(t0) {
  const $ = _c(4);
  const {
    triggerClassName,
    labelMode: t1
  } = t0;
  const labelMode = t1 === undefined ? "full" : t1;
  const t2 = useCurrentLocale();
  let t3;
  if ($[0] !== labelMode || $[1] !== t2 || $[2] !== triggerClassName) {
    t3 = <SwitcherTrigger currentLocale={t2} labelMode={labelMode} triggerClassName={triggerClassName} />;
    $[0] = labelMode;
    $[1] = t2;
    $[2] = triggerClassName;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}
export function RouteLanguageSwitcher({
  align = "end",
  triggerClassName,
  labelMode = "full"
}: RouteLanguageSwitcherProps) {
  const t = useTranslations("MainNav");
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const params = useParams();
  const currentLocale = useCurrentLocale();
  const routeParams = Object.fromEntries(Object.entries(params ?? {}).filter(([key]) => key !== "locale"));
  const query = Object.fromEntries(searchParams.entries());
  const localizedHref = {
    pathname,
    params: routeParams,
    query
  } as Parameters<typeof Link>[0]["href"];
  const persistLocale = (nextLocale: (typeof locales)[number]) => {
    document.cookie = `NEXT_LOCALE=${nextLocale}; path=/; max-age=31536000; samesite=lax`;
  };
  return <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <SwitcherTrigger currentLocale={currentLocale} labelMode={labelMode} triggerClassName={triggerClassName} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align}>
        <DropdownMenuLabel>{t("changeLanguage")}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {locales.map(locale => <DropdownMenuItem asChild key={locale}>
            <Link href={localizedHref} locale={locale} className="cursor-pointer" onClick={() => persistLocale(locale)}>
              {localeLabels[locale]}
            </Link>
          </DropdownMenuItem>)}
      </DropdownMenuContent>
    </DropdownMenu>;
}